import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IconLeft, IconDelete, IconRight, IconDeleteDisabled } from "@assets";
import { Button, Form, message, Select, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { CheckOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { v4 as uuid } from "uuid";
import { api } from "@api";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedVariants,
  setVariantToEdit,
} from "@store/product/productSlice";
import { useTranslation } from "react-i18next";

const RootContainer = styled.div`
  position: relative;
  height: 100%;

  .ant-form {
    .buttons-wrapper {
      position: absolute;
      top: -70px;
      right: 12px;
    }

    .ant-btn {
      height: 48px;
      width: 172px;
    }
  }
`;

const Container = styled.div`
  border-radius: 8px;
  padding: 24px;
  background: ${(props) => props.theme.backgroundColor};
`;

const Topbar = styled.div`
  padding-top: 16px;
  padding-bottom: 24px;
  display: flex;
  gap: 18px;
  align-items: center;
  justify-content: center;

  .ant-btn-default {
    height: 48px;
    padding: 0 12px !important;
    border: none !important;
    background: ${(props) => props.theme.backgroundColor} !important;
    color: ${(props) => props.theme.primaryColor} !important;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }
  
  @media (max-width: 480px){
  flex-wrap:wrap;
  justify-content:start;
}
`;

const TitleWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  white-space:nowrap;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 400;
  color: #1d1014;
  line-height: 1;
`;

const GrayTitle = styled(Title)`
  color: #868686;
`;

const TitleBar = styled.div`
  padding-bottom: 16px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${Title} {
    flex: 1;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }

  @media (max-width: 480px){
flex-direction:column;
gap:10px;
justify-content:start;
align-items:start;
}

`;

const ItemCard = styled.div`
  padding: 24px;
  margin-bottom: 24px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  background-color: #fff;

  .ant-form {
    flex: 1;
    width: 100%;
    border-radius: 8px;
    background: #fff;
    padding: 0;

    .ant-input {
      width: 100%;
    }

    .ant-form-item {
      margin-bottom: 24px;

      :last-child {
        margin: 0;
      }
    }
  }

  .icon-delete {
    width: 48px;
    min-width: 48px;
    height: 48px;
    cursor: pointer;
  }

  .ant-select-multiple .ant-select-selection-placeholder {
    inset-inline-start: 0 !important;
  }
`;

const SpinWrapper = styled.div`
  height: 80%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const Item = ({
  options,
  item,
  variant_to_edit,
  disabledDelete,
  handleUpdateClicked,
  handleDeleteClicked,
}) => {
  const [form] = Form.useForm();
  const [selected_option, setSelectedOption] = useState(null);

  useEffect(() => {
    if (variant_to_edit && options.length > 0) {
      form.setFieldValue("name_id", variant_to_edit.option_name?.id);
      form.setFieldValue(
        "value_ids",
        variant_to_edit.option_values?.map((o) => o.id)
      );
      setSelectedOption(variant_to_edit.option_name);
    }
  }, [variant_to_edit, options]);

  const onFieldsChange = (changedFields) => {
    let { name_id, value_ids } = form.getFieldsValue();
    let option_name = options.find((o) => o.id === name_id);
    let option_values = option_name?.VariantValues?.filter((v) =>
      value_ids?.includes(v.id)
    );

    handleUpdateClicked({
      id: item.id,
      option_name,
      option_values,
    });

    setSelectedOption(options.find((o) => o.id === name_id));
    if (changedFields[0]?.name[0] === "name_id") {
      form.setFieldValue("value_ids", null);
    }
  };

  const {t} = useTranslation();

  return (
    <ItemCard>
      <Form layout="vertical" form={form} onFieldsChange={onFieldsChange}>
        <Form.Item name={"name_id"}>
          <Select
            placeholder={t('option_name')}
            options={options.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            disabled={variant_to_edit}
            style={{ height: "fit-content" }}
          />
        </Form.Item>
        <Form.Item name={"value_ids"}>
          <Select
            mode="multiple"
            placeholder="Value"
            allowClear
            options={selected_option?.VariantValues?.map((v) => ({
              value: v.id,
              label: v.name,
            }))}
            style={{ height: "fit-content" }}
          />
        </Form.Item>
      </Form>
      {disabledDelete ? (
        <IconDeleteDisabled className="icon-delete" />
      ) : (
        <IconDelete
          className="icon-delete"
          onClick={() => handleDeleteClicked(item)}
        />
      )}
    </ItemCard>
  );
};

const AddOptions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selected_variants, variant_to_edit } = useSelector(
    (state) => state.product
  );
  const [options, setOptions] = useState([]);
  const [data, setData] = useState([
    {
      id: uuid(),
      option_name: null,
      option_values: [],
    },
  ]);
  const [disabledSave, setDisabledSave] = useState(true);

  const {t} = useTranslation();

  useEffect(() => {
    getProductOptions();
  }, []);

  useEffect(() => {
    const isEmpty = (value) => !value || value.length === 0;
    const hasEmpty = data.some(
      (item) => isEmpty(item.option_name) || isEmpty(item.option_values)
    );
    setDisabledSave(hasEmpty || data.length === 0);
  }, [data]);

  const getProductOptions = () => {
    api
      .getProductOptions()
      .then(({ data }) => {
        if (variant_to_edit || selected_variants.length === 0) {
          setOptions(data);
        } else {
          setOptions(
            data.filter(
              (o) => !selected_variants.some((v) => v.option_name.id === o.id)
            )
          );
        }
      })
      .catch((error) => {
        message.error(
          t("fail_load_option") + (error.message || t('unknown_error'))
        );
      });
  };

  const handleSave = () => {
    let _selected_variants = [];
    if (variant_to_edit) {
      // Replace the existing variant being edited
      _selected_variants = selected_variants.map((v) => {
        if (v.option_name.id === data[0]?.option_name?.id) {
          return data[0];
        }
        return v;
      });
    } else {
      // Append new options in the order they were added
      _selected_variants = [...selected_variants, ...data];
      // No sorting — preserve order!
    }

    dispatch(setSelectedVariants(_selected_variants));
    message.success(t('added_options_success'));
    goBack();
  };

  const handleAddOptionClicked = () => {
    setData((prev) => [
      ...prev,
      {
        id: uuid(),
        option_name: null,
        option_values: [],
      },
    ]);
  };

  const handleUpdateClicked = (item) => {
    setData((prev) => prev.map((x) => (x.id === item.id ? item : x)));
  };

  const handleDeleteClicked = (item) => {
    if (data.length === 1) return;
    setData((prev) => prev.filter((x) => x.id !== item.id));
  };

  const goBack = () => {
    navigate(-1);
    dispatch(setVariantToEdit(null));
  };

  const Loader = () => (
    <SpinWrapper>
      <Spin />
    </SpinWrapper>
  );

  return (
    <RootContainer>
      <Topbar>
        <Button type="default" icon={<IconLeft />} onClick={goBack}>
          {t('back')}
        </Button>
        <TitleWrapper>
          <GrayTitle>{t('products')}</GrayTitle>
          <IconRight />
          <GrayTitle>{t('create_product')}</GrayTitle>
          <IconRight />
          <Title>{t('add_option')}</Title>
        </TitleWrapper>
        <Button
          type="primary"
          disabled={disabledSave}
          icon={<CheckOutlined />}
          onClick={handleSave}
        >
          {variant_to_edit ? t('save') : t('create')}
        </Button>
      </Topbar>
      <Container>
        {!variant_to_edit && (
          <TitleBar>
            <Title>{t('product_options_eg')}</Title>
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={handleAddOptionClicked}
            >
              {t('add_option')}
            </Button>
          </TitleBar>
        )}
        {data.map((item) => (
          <Item
            key={item.id}
            options={options}
            item={item}
            variant_to_edit={variant_to_edit}
            disabledDelete={data.length === 1}
            handleUpdateClicked={handleUpdateClicked}
            handleDeleteClicked={handleDeleteClicked}
          />
        ))}
      </Container>
    </RootContainer>
  );
};

export default AddOptions;
